/**
 * Bausteinsammlung der Kanzlei.
 *
 * Zwei Arten, ein Zweck: nichts zweimal bauen.
 *
 *  - **Abschnitte** sind im Editor gebaute Blöcke — ein Hinweiskasten, ein
 *    zweispaltiger Leistungsblock, ein Fußbereich. Sie landen in der
 *    Bausteinauswahl und werden per Ziehen eingesetzt.
 *  - **Textbausteine** sind wiederkehrende Formulierungen — Grußformeln,
 *    Haftungshinweise, Fristenformeln. Sie werden in den gerade bearbeiteten
 *    Text eingesetzt.
 *
 * Der Nutzen entsteht über die Zeit: nach drei Aussendungen ist die Sammlung
 * das, was die vierte auf zwanzig Minuten verkürzt.
 */

import { Bausteine } from './api.js';
import { el, ersetzen, knopf, laedt, meldung, dialog, eingabefeld } from './ui.js';
import { symbolFuer } from './baustein-symbole.js';

/** Kennung, unter der eigene Bausteine im Blockmanager liegen. */
const VORSATZ = 'eigen-';

/**
 * Trägt die gespeicherten Abschnitte in die Bausteinauswahl ein.
 *
 * @param {any} editor
 * @returns {Promise<number>} Anzahl der eingetragenen Bausteine.
 */
export async function eigeneBausteineLaden(editor) {
  /** @type {any[]} */
  let eintraege = [];
  try {
    eintraege = await Bausteine.liste('abschnitt');
  } catch {
    // Ohne Sammlung arbeitet der Editor weiter — sie ist eine Annehmlichkeit,
    // keine Voraussetzung.
    return 0;
  }

  const verwaltung = editor.BlockManager;

  // Erst die alten entfernen: nach dem Speichern eines neuen Bausteins wird
  // neu geladen, und ohne das Aufräumen stünde jeder doppelt da.
  for (const baustein of [...verwaltung.getAll().models]) {
    if (baustein.getId().startsWith(VORSATZ)) verwaltung.remove(baustein.getId());
  }

  for (const eintrag of eintraege) {
    verwaltung.add(`${VORSATZ}${eintrag.id}`, {
      label: eintrag.name,
      category: { id: 'eigene', label: 'Eigene Bausteine', open: true },
      content: eintrag.inhalt,
      media: symbolFuer('kanzlei-hinweis'),
    });
  }
  return eintraege.length;
}

/**
 * Speichert den ausgewählten Abschnitt als eigenen Baustein.
 *
 * @param {any} editor
 * @param {(meldung: HTMLElement) => void} melden
 * @returns {Promise<boolean>} true, wenn gespeichert wurde.
 */
export async function abschnittSpeichern(editor, melden) {
  const gewaehlt = editor.getSelected();
  if (!gewaehlt) {
    melden(
      meldung(
        'warnung',
        'Bitte zuerst den Abschnitt anklicken, der gespeichert werden soll — dann erneut versuchen.',
      ),
    );
    return false;
  }

  // Der MJML-Quelltext genau dieses Bausteins, nicht der ganzen Vorlage.
  const inhalt = gewaehlt.toHTML();
  if (!inhalt || inhalt.length < 10) {
    melden(meldung('warnung', 'Der ausgewählte Baustein hat keinen speicherbaren Inhalt.'));
    return false;
  }

  return new Promise((aufloesen) => {
    const { feld: nameFeld, eingabe: nameEingabe } = eingabefeld({
      beschriftung: 'Name des Bausteins',
      platzhalter: 'z. B. Hinweiskasten Fristen',
      hinweis: 'Unter diesem Namen erscheint er künftig in der Bausteinauswahl.',
    });
    const meldungsBereich = el('div');

    const schliessen = dialog({
      titel: 'Als eigenen Baustein speichern',
      inhalt: [
        el('p', {
          text: `Gespeichert wird der ausgewählte Baustein (${beschreiben(gewaehlt)}) mit seiner gesamten Gestaltung.`,
        }),
        nameFeld,
        meldungsBereich,
      ],
      aktionen: [
        knopf('Abbrechen', {
          beiKlick: () => {
            schliessen();
            aufloesen(false);
          },
        }),
        knopf('Speichern', {
          art: 'haupt',
          beiKlick: async () => {
            const name = nameEingabe.value.trim();
            if (!name) {
              ersetzen(meldungsBereich, meldung('warnung', 'Bitte einen Namen vergeben.'));
              return;
            }
            ersetzen(meldungsBereich, laedt('Wird gespeichert …'));
            try {
              await Bausteine.anlegen({ art: 'abschnitt', name, inhalt });
              await eigeneBausteineLaden(editor);
              schliessen();
              melden(meldung('erfolg', `Baustein „${name}" gespeichert.`));
              aufloesen(true);
            } catch (fehler) {
              ersetzen(
                meldungsBereich,
                meldung(
                  'fehler',
                  fehler instanceof Error ? fehler.message : 'Speichern fehlgeschlagen',
                ),
              );
            }
          },
        }),
      ],
    });
  });
}

/**
 * @param {any} komponente
 * @returns {string}
 */
function beschreiben(komponente) {
  const art = String(komponente.get('type') ?? '');
  /** @type {Record<string, string>} */
  const namen = {
    'mj-section': 'Abschnitt',
    'mj-column': 'Spalte',
    'mj-text': 'Textbaustein',
    'mj-image': 'Bild',
    'mj-button': 'Schaltfläche',
    'mj-divider': 'Trennlinie',
  };
  return namen[art] ?? art ?? 'Baustein';
}

/**
 * Baut den Bereich für Textbausteine.
 *
 * @param {object} optionen
 * @param {(text: string) => void} optionen.beiAuswahl  Setzt den Text ein.
 * @returns {HTMLElement}
 */
export function textbausteinBereich({ beiAuswahl }) {
  const behaelter = el('div');
  const liste = el('div');
  const meldungsBereich = el('div');

  async function zeichnen() {
    ersetzen(liste, laedt('Textbausteine werden geladen …'));
    try {
      const eintraege = await Bausteine.liste('text');
      ersetzen(
        liste,
        eintraege.length
          ? el(
              'div.auswahl',
              eintraege.map((/** @type {any} */ eintrag) =>
                el('div.auswahl-eintrag.anklickbar', [
                  el('span', {
                    onclick: () => beiAuswahl(eintrag.inhalt),
                    style: 'cursor:pointer;flex:1',
                  }, [
                    el('span.haupttext', { text: eintrag.name }),
                    el('span.nebentext', { text: kuerzen(eintrag.inhalt) }),
                  ]),
                  el('button.still', {
                    type: 'button',
                    text: '×',
                    title: 'Löschen',
                    'aria-label': `Textbaustein „${eintrag.name}" löschen`,
                    onclick: async () => {
                      await Bausteine.loeschen(eintrag.id);
                      await zeichnen();
                    },
                  }),
                ]),
              ),
            )
          : el('p.leer', { text: 'Noch keine Textbausteine angelegt.' }),
      );
    } catch (fehler) {
      ersetzen(
        liste,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Nicht ladbar'),
      );
    }
  }

  const anlegen = () => {
    const { feld: nameFeld, eingabe: nameEingabe } = eingabefeld({
      beschriftung: 'Name',
      platzhalter: 'z. B. Grußformel',
    });
    const { feld: textFeld, eingabe: textEingabe } = eingabefeld({
      beschriftung: 'Text',
      art: 'textarea',
      platzhalter: 'Mit freundlichen Grüßen\nIhr Team der G&W Steuerberatung',
    });
    const dialogMeldung = el('div');

    const schliessen = dialog({
      titel: 'Textbaustein anlegen',
      inhalt: [nameFeld, textFeld, dialogMeldung],
      aktionen: [
        knopf('Abbrechen', { beiKlick: () => schliessen() }),
        knopf('Speichern', {
          art: 'haupt',
          beiKlick: async () => {
            const name = nameEingabe.value.trim();
            const inhalt = textEingabe.value.trim();
            if (!name || !inhalt) {
              ersetzen(dialogMeldung, meldung('warnung', 'Name und Text werden beide gebraucht.'));
              return;
            }
            try {
              await Bausteine.anlegen({ art: 'text', name, inhalt });
              schliessen();
              await zeichnen();
            } catch (fehler) {
              ersetzen(
                dialogMeldung,
                meldung(
                  'fehler',
                  fehler instanceof Error ? fehler.message : 'Speichern fehlgeschlagen',
                ),
              );
            }
          },
        }),
      ],
    });
  };

  behaelter.append(
    el('p.editor-hinweis', {
      text: 'Wiederkehrende Formulierungen. Ein Klick setzt den Text beim ausgewählten Textbaustein ein.',
    }),
    el('div.knopf-reihe', [knopf('Textbaustein anlegen', { beiKlick: anlegen })]),
    meldungsBereich,
    el('div', { style: 'height:10px' }),
    liste,
  );

  zeichnen();
  return behaelter;
}

/**
 * @param {string} text
 * @returns {string}
 */
function kuerzen(text) {
  const eine = text.replace(/\s+/g, ' ').trim();
  return eine.length > 70 ? `${eine.slice(0, 67)}…` : eine;
}
